import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Crosshair, Mountain, Upload, CheckSquare, RefreshCw,
  TrendingUp, Minus, AlertTriangle, FileDown, Plus,
  Map, Search, Edit3, Trash2, X, MapPin, Save, Layers,
  ChevronRight,
} from 'lucide-react';
import { geodesieApi, sigApi } from '../../api/client';
import { useMapLayer, ADMIN_TILE_LAYERS } from '../../layouts/AdminGeoLayout';
import './AdminGeoDashboard.css';

/* ── Helpers ────────────────────────────────────────────────── */
const fmtN  = (n) => (n == null ? '—' : Number(n).toLocaleString('fr-FR'));
const fmtC  = (v, d = 6) => (v != null ? Number(v).toFixed(d) : '—');

/* geodesieApi.list() renvoie une Feature GeoJSON par point (coordonnées WGS84
   + laborde imbriquées) — le reste du composant travaille sur des objets
   plats, donc on aplatit ici une fois pour toutes. */
function normalizeGeoPoint(feature) {
  const [longitude, latitude] = feature.geometry?.coordinates || [];
  const [x_laborde, y_laborde] = feature.properties?.laborde?.coordinates || [];
  return {
    id: feature.id,
    nom: feature.properties?.nom,
    id_point: feature.properties?.id_point,
    ordre: feature.properties?.ordre,
    precision: feature.properties?.precision,
    pdf_file: feature.properties?.pdf_file,
    date_ajout: feature.properties?.date_ajout,
    latitude, longitude, x_laborde, y_laborde,
  };
}

/* ── Marker styles ──────────────────────────────────────────── */
const MK_NORMAL = {
  radius: 5, fillColor: '#22c55e', color: '#15803d',
  weight: 1.5, fillOpacity: 0.85, opacity: 1,
};
const MK_SELECTED = {
  radius: 9, fillColor: '#f59e0b', color: '#b45309',
  weight: 2.5, fillOpacity: 1, opacity: 1,
};

function popupHtml(pt) {
  const row = (l, v) =>
    `<tr><td style="color:#64748b;padding:2px 8px 2px 0;white-space:nowrap">${l}</td>` +
    `<td style="font-family:monospace;font-size:11px">${v}</td></tr>`;
  return `<div style="font-family:Inter,system-ui,sans-serif;min-width:190px">
    <div style="font-weight:700;font-size:13px;margin-bottom:8px;color:#1e293b">${pt.nom || '—'}</div>
    <table style="border-collapse:collapse;width:100%;font-size:12px">
      ${row('Code',    pt.id_point || '—')}
      ${row('Ordre',   pt.ordre != null ? 'R' + pt.ordre : '—')}
      ${row('Lat.',    fmtC(pt.latitude))}
      ${row('Lon.',    fmtC(pt.longitude))}
      ${row('X Lab.', fmtC(pt.x_laborde, 2))}
      ${row('Y Lab.', fmtC(pt.y_laborde, 2))}
    </table>
  </div>`;
}

/* ── Edit Modal ─────────────────────────────────────────────── */
function EditModal({ point, onSave, onClose }) {
  const [form, setForm] = useState({
    nom:       point.nom       ?? '',
    id_point:  point.id_point  ?? '',
    ordre:     point.ordre     != null ? String(point.ordre)     : '',
    precision: point.precision != null ? String(point.precision) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const Field = ({ k, label, type = 'text' }) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 11, color: 'var(--ag-text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
        {label}
      </label>
      <input
        type={type}
        value={form[k]}
        onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
        style={{
          width: '100%', background: 'var(--c-bg-80)',
          border: '1px solid var(--c-border-dk)',
          color: 'var(--ag-text)', borderRadius: 6, padding: '7px 10px',
          fontSize: 13, boxSizing: 'border-box', outline: 'none',
          fontFamily: 'inherit',
        }}
      />
    </div>
  );

  const ROField = ({ label, value }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, color: 'var(--ag-text-muted)', fontWeight: 600, marginBottom: 3 }}>{label}</div>
      <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ag-text-muted)' }}>{value ?? '—'}</div>
    </div>
  );

  const save = async () => {
    if (!form.nom.trim()) { setError('Le nom est requis.'); return; }
    setSaving(true); setError('');
    try {
      const { data } = await geodesieApi.update(point.id, {
        nom:      form.nom.trim(),
        id_point: form.id_point.trim(),
        ordre:     form.ordre     !== '' ? parseInt(form.ordre,     10) : null,
        precision: form.precision !== '' ? parseInt(form.precision, 10) : null,
      });
      onSave(normalizeGeoPoint(data));
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-header">
          <Edit3 size={15} color="var(--ag-green)" />
          <span>Modifier le point géodésique</span>
          <button onClick={onClose} className="modal-close"><X size={16} /></button>
        </div>

        <Field k="nom"      label="Nom" />
        <Field k="id_point" label="Code / ID point" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><Field k="ordre"     label="Ordre (0 = R0, 1 = R1…)" type="number" /></div>
          <div><Field k="precision" label="Précision"                type="number" /></div>
        </div>

        <div style={{ borderTop: '1px solid var(--c-border)', paddingTop: 14, marginTop: 4 }}>
          <div style={{ fontSize: 10, color: 'var(--ag-text-muted)', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 10 }}>
            COORDONNÉES — lecture seule (réimporter pour modifier)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <ROField label="Latitude"  value={fmtC(point.latitude)} />
            <ROField label="Longitude" value={fmtC(point.longitude)} />
            <ROField label="X Laborde" value={fmtC(point.x_laborde, 2)} />
            <ROField label="Y Laborde" value={fmtC(point.y_laborde, 2)} />
          </div>
        </div>

        {error && <div style={{ color: 'var(--ag-red)', fontSize: 12, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={onClose} className="modal-btn-ghost">Annuler</button>
          <button onClick={save} disabled={saving} className="modal-btn-primary">
            <Save size={13} /> {saving ? 'Sauvegarde…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Delete Modal ───────────────────────────────────────────── */
function DeleteModal({ point, onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await onConfirm(point.id); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ borderColor: 'rgba(239,68,68,0.3)', maxWidth: 380 }}>
        <div className="modal-header">
          <Trash2 size={15} color="var(--ag-red)" />
          <span>Supprimer le point</span>
          <button onClick={onClose} className="modal-close"><X size={16} /></button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--ag-text-muted)', margin: '0 0 20px' }}>
          Supprimer définitivement <strong style={{ color: 'var(--ag-text)' }}>{point.nom}</strong>
          {point.id_point ? ` (${point.id_point})` : ''} ? Cette action est irréversible.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="modal-btn-ghost">Annuler</button>
          <button onClick={go} disabled={busy} className="modal-btn-danger">
            <Trash2 size={13} /> {busy ? 'Suppression…' : 'Supprimer'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Point list item ────────────────────────────────────────── */
function PointRow({ point, selected, onSelect, onEdit, onDelete, rowRef }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      ref={rowRef}
      onClick={onSelect}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className={`pt-row${selected ? ' pt-row-active' : ''}`}
    >
      <div className={`pt-dot${selected ? ' pt-dot-sel' : ''}`} />
      <div className="pt-info">
        <div className="pt-nom">{point.nom}</div>
        <div className="pt-sub">
          {point.id_point && <span className="pt-code">{point.id_point}</span>}
          {point.ordre != null && (
            <span className="pt-ordre">R{point.ordre}</span>
          )}
        </div>
      </div>
      <div className="pt-actions" style={{ opacity: (hov || selected) ? 1 : 0 }}>
        <button
          title="Modifier"
          onClick={e => { e.stopPropagation(); onEdit(); }}
          className="pt-act-btn pt-act-edit"
        ><Edit3 size={11} /></button>
        <button
          title="Supprimer"
          onClick={e => { e.stopPropagation(); onDelete(); }}
          className="pt-act-btn pt-act-del"
        ><Trash2 size={11} /></button>
      </div>
    </div>
  );
}

/* ── Quick actions ──────────────────────────────────────────── */
const QUICK = [
  { icon: Plus,          label: 'Importer fiches FTM (PDF / Word)', bg: 'var(--c-a15)',           to: '/admin-geo/geodesy' },
  { icon: Upload,        label: 'Importer limites administratives',  bg: 'rgba(96,165,250,0.15)', to: '/admin-geo/limites' },
  { icon: FileDown,      label: 'Export rapport qualité',            bg: 'rgba(167,139,250,0.15)',to: '/admin-geo/import'  },
  { icon: AlertTriangle, label: 'Voir les alertes',                  bg: 'rgba(248,113,113,0.15)',to: '/admin-geo/audit'   },
];

/* ═══════════════════════════════════════════════════════════════
   DASHBOARD
═══════════════════════════════════════════════════════════════ */
export default function AdminGeoDashboard() {
  const navigate = useNavigate();

  /* ── Refs ── */
  const activeLayer     = useMapLayer();
  const mapRef          = useRef(null);
  const mapInst         = useRef(null);
  const tileLayerRef    = useRef(null);
  const markersRef      = useRef({});
  const layerRef        = useRef(null);
  const selIdRef        = useRef(null);
  const pointsRef       = useRef([]);
  const rowRefs         = useRef({});
  const selectFnRef     = useRef(null);
  const limLayersRef    = useRef({});
  const selectedLimRef  = useRef(null);

  /* ── State ── */
  const [tab,          setTab]         = useState('overview');
  const [points,       setPoints]      = useState([]);
  const [statsGeo,     setStatsGeo]    = useState(null);
  const [statsSig,     setStatsSig]    = useState(null);
  const [loading,      setLoading]     = useState(true);
  const [search,       setSearch]      = useState('');
  const [selectedId,   setSelectedId]  = useState(null);
  const [editPt,       setEditPt]      = useState(null);
  const [deletePt,     setDeletePt]    = useState(null);
  const [lastRefresh,  setLastRefresh] = useState(new Date());
  const [layerVisible, setLayerVis]    = useState(true);
  const [limVis,       setLimVis]      = useState({ region: false, district: false, commune: false, fokontany: false });

  /* ── Filtered list ── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return points;
    return points.filter(p =>
      p.nom?.toLowerCase().includes(q) ||
      p.id_point?.toLowerCase().includes(q)
    );
  }, [points, search]);

  const selectedPt = useMemo(() => points.find(p => p.id === selectedId), [points, selectedId]);

  /* ── Build / rebuild markers ── */
  const buildMarkers = useCallback((pts, map) => {
    const L = window.L;
    if (!L || !map) return;
    if (!layerRef.current) {
      layerRef.current = L.layerGroup().addTo(map);
    } else {
      layerRef.current.clearLayers();
    }
    markersRef.current = {};
    pts.forEach(pt => {
      if (pt.latitude == null || pt.longitude == null) return;
      const m = L.circleMarker([pt.latitude, pt.longitude], { ...MK_NORMAL })
        .bindPopup(popupHtml(pt), { maxWidth: 280 })
        .on('click', () => selectFnRef.current?.(pt.id, false));
      layerRef.current.addLayer(m);
      markersRef.current[pt.id] = m;
    });
  }, []);

  /* ── Select a point (list → map or map → list) ── */
  const selectPoint = useCallback((id, flyTo = true) => {
    // Reset previous marker style
    if (selIdRef.current && markersRef.current[selIdRef.current]) {
      markersRef.current[selIdRef.current].setStyle(MK_NORMAL);
    }

    selIdRef.current = id;
    setSelectedId(id);

    const marker = markersRef.current[id];
    if (marker) {
      marker.setStyle(MK_SELECTED);
      if (!marker.isPopupOpen()) marker.openPopup();
    }

    if (flyTo) {
      const pt = pointsRef.current.find(p => p.id === id);
      if (pt?.latitude && pt?.longitude && mapInst.current) {
        mapInst.current.flyTo([pt.latitude, pt.longitude], 15, { duration: 0.7 });
      }
    }

    // Switch to list tab and scroll item into view
    setTab('points');
    setTimeout(() => {
      rowRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 150);
  }, []);

  // Keep ref in sync so Leaflet event handlers always call the latest version
  selectFnRef.current = selectPoint;

  /* ── Load all data ──
     Les points (critiques pour l'affichage) sont chargés séparément des
     stats geo/sig : un échec sur les stats (ou sur sig, app annexe) ne doit
     plus empêcher l'affichage des points, contrairement à l'ancien
     Promise.all qui faisait tout échouer d'un coup. */
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const lRes = await geodesieApi.list({ size: 500 });
      const pts = (lRes.data?.data?.features || []).map(normalizeGeoPoint);
      pointsRef.current = pts;
      setPoints(pts);
      if (mapInst.current) buildMarkers(pts, mapInst.current);
    } catch (err) {
      console.error('Chargement points :', err);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
    geodesieApi.stats().then(r => setStatsGeo(r.data)).catch(err => console.error('Stats géodésie :', err));
    sigApi.stats().then(r => setStatsSig(r.data)).catch(err => console.error('Stats SIG :', err));
  }, [buildMarkers]);

  /* ── Toggle couche limite (charge GeoJSON si 1ère activation) ── */
  const LIM_COLORS = {
    region: '#3b82f6', district: '#10b981',
    commune: '#f59e0b', fokontany: '#ef4444',
  };

  const toggleLimLayer = useCallback(async (type, visible) => {
    const L = window.L;
    if (!L || !mapInst.current) return;

    setLimVis(prev => ({ ...prev, [type]: visible }));

    if (!visible) {
      if (limLayersRef.current[type]) mapInst.current.removeLayer(limLayersRef.current[type]);
      return;
    }

    // Déjà chargé → réafficher
    if (limLayersRef.current[type]) {
      limLayersRef.current[type].addTo(mapInst.current);
      return;
    }

    try {
      const { data } = await sigApi.geojson(type);
      if (!data.features?.length) return;
      const color        = LIM_COLORS[type];
      const defaultStyle  = { color, weight: 2, fillOpacity: 0, opacity: 0.9 };
      const selectedStyle = { color: '#ffffff', weight: 3, fillOpacity: 0, opacity: 1 };

      const geoLayer = L.geoJSON(data, {
        style: defaultStyle,
        onEachFeature: (feature, layer) => {
          layer.bindTooltip(feature.properties.nom || '—', { sticky: true, className: 'agd-tip' });
          layer.on('click', () => {
            if (selectedLimRef.current && selectedLimRef.current !== layer)
              selectedLimRef.current.setStyle(defaultStyle);
            layer.setStyle(selectedStyle);
            selectedLimRef.current = layer;
          });
        },
      }).addTo(mapInst.current);
      limLayersRef.current[type] = geoLayer;
    } catch (e) {
      console.error('GeoJSON limites :', e);
      setLimVis(prev => ({ ...prev, [type]: false }));
    }
  }, []);

  /* ── Init Leaflet ── */
  useEffect(() => {
    if (mapInst.current || !mapRef.current) return;
    const L = window.L;
    if (!L) return;

    const map = L.map(mapRef.current, {
      center: [-18.9, 47.5], zoom: 7, zoomControl: false,
    });
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    const initTile = ADMIN_TILE_LAYERS[activeLayer] || ADMIN_TILE_LAYERS.dark;
    tileLayerRef.current = L.tileLayer(initTile.url, {
      attribution: initTile.attribution,
      subdomains: initTile.subdomains || 'abc',
      className: initTile.className || '',
      maxZoom: 19,
    }).addTo(map);

    mapInst.current = map;

    // Points may have loaded before map init
    if (pointsRef.current.length > 0) buildMarkers(pointsRef.current, map);

    return () => {
      map.remove();
      mapInst.current  = null;
      layerRef.current = null;
      markersRef.current = {};
    };
  }, [buildMarkers]);

  /* ── Swap tile layer when activeLayer changes ── */
  useEffect(() => {
    const map = mapInst.current;
    const L   = window.L;
    if (!map || !L) return;
    const tile = ADMIN_TILE_LAYERS[activeLayer] || ADMIN_TILE_LAYERS.dark;
    if (tileLayerRef.current) map.removeLayer(tileLayerRef.current);
    tileLayerRef.current = L.tileLayer(tile.url, {
      attribution: tile.attribution,
      subdomains: tile.subdomains || 'abc',
      className: tile.className || '',
      maxZoom: 19,
    }).addTo(map);
  }, [activeLayer]);

  /* ── First load ── */
  useEffect(() => { loadData(); }, [loadData]);

  /* ── Toggle layer visibility ── */
  useEffect(() => {
    if (!mapInst.current || !layerRef.current) return;
    if (layerVisible) {
      if (!mapInst.current.hasLayer(layerRef.current))
        mapInst.current.addLayer(layerRef.current);
    } else {
      mapInst.current.removeLayer(layerRef.current);
    }
  }, [layerVisible]);

  /* ── Edit save ── */
  const handleSaveEdit = useCallback((updated) => {
    setPoints(prev => prev.map(p => p.id === updated.id ? updated : p));
    pointsRef.current = pointsRef.current.map(p => p.id === updated.id ? updated : p);
    if (markersRef.current[updated.id]) {
      markersRef.current[updated.id].setPopupContent(popupHtml(updated));
    }
    setEditPt(null);
  }, []);

  /* ── Delete confirm ── */
  const handleDelete = useCallback(async (id) => {
    await geodesieApi.remove(id);
    setPoints(prev => prev.filter(p => p.id !== id));
    pointsRef.current = pointsRef.current.filter(p => p.id !== id);
    if (markersRef.current[id]) {
      layerRef.current?.removeLayer(markersRef.current[id]);
      delete markersRef.current[id];
    }
    if (selIdRef.current === id) {
      selIdRef.current = null;
      setSelectedId(null);
    }
    setStatsGeo(prev => prev ? { ...prev, total: Math.max(0, prev.total - 1) } : prev);
    setDeletePt(null);
  }, []);

  /* ── Clear selection ── */
  const clearSelection = useCallback(() => {
    if (selIdRef.current && markersRef.current[selIdRef.current]) {
      markersRef.current[selIdRef.current].setStyle(MK_NORMAL);
      markersRef.current[selIdRef.current].closePopup();
    }
    selIdRef.current = null;
    setSelectedId(null);
  }, []);

  /* ═════════════════════════════════════════════════════════
     RENDER
  ═════════════════════════════════════════════════════════ */
  return (
    <div className="agd">

      {/* ── Modals ── */}
      {editPt   && <EditModal   point={editPt}   onSave={handleSaveEdit} onClose={() => setEditPt(null)} />}
      {deletePt && <DeleteModal point={deletePt} onConfirm={handleDelete} onClose={() => setDeletePt(null)} />}

      {/* ── Header ── */}
      <div className="agd-header">
        <div className="agd-header-left">
          <h1>Tableau de bord SIG</h1>
          <p>Mis à jour {lastRefresh.toLocaleTimeString('fr-FR')} · Madagascar · Système Laborde</p>
        </div>
        <div className="agd-header-actions">
          <button className="agd-btn agd-btn-ghost" onClick={loadData} disabled={loading}>
            <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            Actualiser
          </button>
          <button className="agd-btn agd-btn-ghost" onClick={() => navigate('/admin-geo/limites')}>
            <Upload size={13} /> Limites admin.
          </button>
          <button className="agd-btn agd-btn-primary" onClick={() => navigate('/admin-geo/geodesy')}>
            <Plus size={13} /> Importer points
          </button>
        </div>
      </div>

      {/* ── Map zone ── */}
      <div className="agd-map-zone">

        {/* Stat pills */}
        <div className="agd-map-overlay">
          <div
            className="agd-stat-pill agd-stat-pill-btn"
            onClick={() => setTab('points')}
            title="Voir la liste des points"
          >
            <div className="agd-stat-pill-icon" style={{ background: 'var(--c-a15)' }}>
              <Crosshair size={12} color="var(--ag-green)" />
            </div>
            <span className="agd-stat-pill-val">
              {loading ? '…' : fmtN(statsGeo?.total)}
            </span>
            <span style={{ color: 'var(--ag-text-muted)', fontSize: 10.5 }}>Points géodésiques</span>
            <ChevronRight size={11} color="var(--ag-text-muted)" />
          </div>
          {statsGeo?.recent_30d > 0 && (
            <div className="agd-stat-pill">
              <span style={{ fontSize: 10, color: 'var(--ag-green)' }}>+{statsGeo.recent_30d} ce mois</span>
            </div>
          )}
        </div>

        {/* Layer switcher */}
        <div className="agd-layer-switcher">
          <h4>Couches visibles</h4>
          <label className="agd-layer-item">
            <input type="checkbox" checked={layerVisible} onChange={e => setLayerVis(e.target.checked)} />
            <div className="agd-layer-dot" style={{ background: 'var(--ag-green)' }} />
            Points géodésiques
            {statsGeo != null && (
              <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--ag-green)', fontWeight: 700 }}>
                {fmtN(statsGeo.total)}
              </span>
            )}
          </label>
          {[
            { key: 'region',    label: 'Régions',    color: '#3b82f6' },
            { key: 'district',  label: 'Districts',  color: '#10b981' },
            { key: 'commune',   label: 'Communes',   color: '#f59e0b' },
            { key: 'fokontany', label: 'Fokontany',  color: '#ef4444' },
          ].map(l => {
            const cnt = statsSig?.[l.key];
            if (!cnt) return null;
            return (
              <label key={l.key} className="agd-layer-item">
                <input
                  type="checkbox"
                  checked={limVis[l.key]}
                  onChange={e => toggleLimLayer(l.key, e.target.checked)}
                />
                <div className="agd-layer-dot" style={{ background: l.color }} />
                {l.label}
                <span style={{ marginLeft: 'auto', fontSize: 10, color: l.color, fontWeight: 700 }}>
                  {fmtN(cnt)}
                </span>
              </label>
            );
          })}
        </div>

        {/* Map */}
        <div ref={mapRef} className="agd-map-container" />

        {/* Selected point info bar */}
        {selectedPt && (
          <div className="agd-infobar">
            <MapPin size={13} color="var(--ag-amber)" style={{ flexShrink: 0 }} />
            <strong className="agd-infobar-nom">{selectedPt.nom}</strong>
            {selectedPt.id_point && (
              <code className="agd-infobar-code">{selectedPt.id_point}</code>
            )}
            {selectedPt.ordre != null && (
              <span className="agd-infobar-ordre">R{selectedPt.ordre}</span>
            )}
            <div className="agd-infobar-coords">
              <span><span className="agd-coord-lbl">Lat </span>{fmtC(selectedPt.latitude)}</span>
              <span><span className="agd-coord-lbl">Lon </span>{fmtC(selectedPt.longitude)}</span>
              <span><span className="agd-coord-lbl">X </span>{fmtC(selectedPt.x_laborde, 2)}</span>
              <span><span className="agd-coord-lbl">Y </span>{fmtC(selectedPt.y_laborde, 2)}</span>
            </div>
            <div className="agd-infobar-actions">
              <button
                onClick={() => setEditPt(selectedPt)}
                className="agd-infobar-btn agd-infobar-btn-edit"
              ><Edit3 size={11} /> Modifier</button>
              <button
                onClick={() => setDeletePt(selectedPt)}
                className="agd-infobar-btn agd-infobar-btn-del"
              ><Trash2 size={11} /> Supprimer</button>
              <button onClick={clearSelection} className="agd-infobar-btn">
                <X size={11} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Right panel ── */}
      <div className="agd-panel">

        {/* Tab bar */}
        <div className="agd-tabs">
          {[
            { id: 'overview', label: 'Vue d\'ensemble' },
            { id: 'points',   label: statsGeo ? `Points (${fmtN(statsGeo.total)})` : 'Points' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`agd-tab${tab === t.id ? ' agd-tab-active' : ''}`}
            >{t.label}</button>
          ))}
        </div>

        {/* ── Overview tab ── */}
        {tab === 'overview' && (
          <div className="agd-tab-content agd-tab-scroll">

            <div className="agd-section">
              <div className="agd-section-title"><Crosshair size={11} />Points géodésiques</div>
              <div className="agd-stats-grid">
                <div className="agd-stat-card">
                  <div className="agd-stat-card-val color-green">
                    {loading ? '…' : fmtN(statsGeo?.total)}
                  </div>
                  <div className="agd-stat-card-label">Total importés</div>
                  {statsGeo?.recent_30d > 0 && (
                    <div className="agd-stat-card-trend trend-up">
                      <TrendingUp size={10} /> +{statsGeo.recent_30d} ce mois
                    </div>
                  )}
                </div>
                <div className="agd-stat-card">
                  <div className="agd-stat-card-val color-amber">
                    {loading ? '…' : fmtN(statsGeo?.recent_30d)}
                  </div>
                  <div className="agd-stat-card-label">Ajoutés (30 j)</div>
                  <div className="agd-stat-card-trend trend-flat">
                    <Minus size={10} /> dernier mois
                  </div>
                </div>
              </div>
            </div>

            <div className="agd-section">
              <div className="agd-section-title"><CheckSquare size={11} />Limites administratives</div>
              {[
                { key: 'region',    label: 'Régions',    color: '#3b82f6' },
                { key: 'district',  label: 'Districts',  color: '#10b981' },
                { key: 'commune',   label: 'Communes',   color: '#f59e0b' },
                { key: 'fokontany', label: 'Fokontany',  color: '#ef4444' },
              ].map(s => (
                <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--c-a06)', fontSize: 12 }}>
                  <span style={{ color: 'var(--ag-text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
                    {s.label}
                  </span>
                  <span style={{ color: statsSig?.[s.key] ? s.color : 'var(--ag-text-muted)', fontWeight: 700 }}>
                    {loading ? '…' : fmtN(statsSig?.[s.key] ?? 0)}
                  </span>
                </div>
              ))}
              <button
                onClick={() => navigate('/admin-geo/limites')}
                style={{ marginTop: 10, fontSize: 11, color: '#818cf8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                Gérer les limites →
              </button>
            </div>

            <div className="agd-section">
              <div className="agd-section-title"><Layers size={11} />Actions rapides</div>
              <div className="agd-quick-actions">
                {QUICK.map(a => (
                  <button key={a.label} className="agd-quick-btn" onClick={() => navigate(a.to)}>
                    <div className="agd-quick-btn-icon" style={{ background: a.bg }}>
                      <a.icon size={13} color="var(--ag-text)" />
                    </div>
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Points tab ── */}
        {tab === 'points' && (
          <div className="agd-tab-content agd-pts-layout">

            {/* Search bar */}
            <div className="agd-search-bar">
              <div style={{ position: 'relative' }}>
                <Search size={13} color="var(--ag-text-muted)" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                <input
                  type="text"
                  placeholder="Rechercher nom, code…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="agd-search-input"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="agd-search-clear">
                    <X size={12} />
                  </button>
                )}
              </div>
              <div className="agd-search-count">
                {loading
                  ? 'Chargement…'
                  : `${filtered.length} / ${points.length} point(s)`}
              </div>
            </div>

            {/* List */}
            <div className="agd-pts-list">
              {loading ? (
                <div className="agd-pts-empty">Chargement des points…</div>
              ) : filtered.length === 0 ? (
                <div className="agd-pts-empty">
                  {search
                    ? 'Aucun résultat pour cette recherche.'
                    : 'Aucun point géodésique importé.'}
                </div>
              ) : (
                filtered.map(pt => (
                  <PointRow
                    key={pt.id}
                    point={pt}
                    selected={pt.id === selectedId}
                    onSelect={() => selectPoint(pt.id)}
                    onEdit={() => setEditPt(pt)}
                    onDelete={() => setDeletePt(pt)}
                    rowRef={el => { rowRefs.current[pt.id] = el; }}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
